import { env } from '@/lib/env';
import { getCsrfToken } from './csrf';
import { refreshSession } from './sessionRefresh';

const API_BASE = `${env.API_BASE}`;

export interface AuthUser {
  uuid: string
  email: string
  name: string | null
  picture: string | null
  roles: string | null
  /** The application-level role of the user (e.g. "ADMIN", "USER"). */
  role: string | null
  /**
   * ISO instant after which the server no longer honours a token refresh: the moment an inactive
   * user is signed out. `AuthProvider` warns two minutes before it and signs out at it.
   */
  sessionExpiresAt: string
}

/** Why a sign-out happened, stashed for the landing page's one-time notice. */
export type SignOutReason = 'timeout'

export const SIGN_OUT_REASON_KEY = 'aerris.signout-reason'

export type SessionResult =
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' }
  | { status: 'unreachable' }

export interface GetSessionOptions {
  /**
   * On a 401 (the ID token has expired), try `POST /auth/refresh` once and, if it is renewed, ask
   * `/auth/me` again. A page load is a real request by a present user, so it counts as activity
   * (docs/plans/session-timeout-fixes.md, S3): without this a reload or new tab anywhere in the
   * refresh grace window showed the user signed out. The deadline probe in `AuthProvider` leaves
   * this OFF — it asks whether the sign-in is alive and must not keep an idle tab alive itself.
   */
  refreshIfExpired?: boolean
}

/**
 * Session check that distinguishes "not signed in" from "server unreachable",
 * so the app shell can show a connection-lost state instead of silently
 * bouncing an active reviewer to the marketing page.
 */
export async function getSession(options: GetSessionOptions = {}): Promise<SessionResult> {
  const first = await fetchSession()
  if (first.status !== 'expired') return first
  if (!options.refreshIfExpired) return { status: 'unauthenticated' }

  const outcome = await refreshSession()
  if (outcome === 'refused') return { status: 'unauthenticated' }
  // Cognito or the server could not be reached: the sign-in may be perfectly good, so this is a
  // lost connection (retryable), not a sign-out.
  if (outcome === 'unavailable') return { status: 'unreachable' }
  const second = await fetchSession()
  return second.status === 'expired' ? { status: 'unauthenticated' } : second
}

/** One `GET /auth/me`; `expired` is the 401 the caller may answer with a refresh. */
async function fetchSession(): Promise<SessionResult | { status: 'expired' }> {
  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    });

    if (response.ok) {
      return { status: 'authenticated', user: await response.json() }
    }
    // A dying backend (5xx) is a connectivity problem, not a sign-out.
    if (response.status >= 500) {
      return { status: 'unreachable' }
    }
    if (response.status === 401) {
      return { status: 'expired' }
    }
    return { status: 'unauthenticated' }
  } catch {
    return { status: 'unreachable' }
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return null
    }

    return response.json()
  } catch {
    return null
  }
}

export async function checkSession(): Promise<boolean> {
  const user = await getCurrentUser()
  return Boolean(user)
}

/**
 * Sign out. A full navigation: the form POST ends the sign-in on the server (refresh token revoked,
 * cookies cleared), which then bounces through Cognito's own logout so "Sign in" asks for a password
 * again, and lands back on the landing page. `reason`, when given, survives the round trip in
 * sessionStorage so the landing page can say why.
 */
export function logout(reason?: SignOutReason): void {
  if (reason) {
    try {
      sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
    } catch {
      // Storage unavailable (privacy mode): the sign-out still happens, only the notice is lost.
    }
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${API_BASE}/auth/logout`;
  // Logout ends the session, so it is a state change the server CSRF-protects like any other; a
  // plain form POST cannot carry a header, so the cookie's token goes in Spring's `_csrf` field.
  const token = getCsrfToken();
  if (token) {
    const csrf = document.createElement('input');
    csrf.type = 'hidden';
    csrf.name = '_csrf';
    csrf.value = token;
    form.appendChild(csrf);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Read — and clear — the reason a `logout(reason)` stashed, so the notice shows exactly once. */
export function consumeSignOutReason(): SignOutReason | null {
  try {
    const stored = sessionStorage.getItem(SIGN_OUT_REASON_KEY);
    if (stored) sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
    return stored === 'timeout' ? stored : null;
  } catch {
    return null;
  }
}

// ── Cached session for the current page load ──────────────────────────────────────────────
// The route guards (beforeLoad) need the caller's role but must not hit /auth/me on every
// navigation. This caches the session for the lifetime of the document and is shared with
// AuthProvider, so the whole app makes a single /auth/me on load. It is safe against session
// timeout precisely because every auth transition here is a full navigation that drops the cache:
// the OAuth login redirect, the logout form POST, and the global 401 handler's
// window.location = '/'. A timed-out session therefore can't be served stale — the next API call
// 401-redirects and the ensuing reload refetches.
let sessionPromise: Promise<SessionResult> | null = null;

/**
 * The session for this page load, fetched once on first read and reused thereafter. An expired
 * token is refreshed here: a load is the user acting.
 */
export function ensureSession(): Promise<SessionResult> {
  if (!sessionPromise) {
    sessionPromise = getSession({ refreshIfExpired: true });
  }
  return sessionPromise;
}

/** Force a fresh /auth/me and replace the cache — after a sign-in or a manual retry. */
export function reloadSession(): Promise<SessionResult> {
  sessionPromise = getSession({ refreshIfExpired: true });
  return sessionPromise;
}

/** Drop the cached session on sign-out so the next read refetches. */
export function clearCachedSession(): void {
  sessionPromise = null;
}

export const authService = {
  checkSession,
  getCurrentUser,
  getSession,
  ensureSession,
  reloadSession,
  clearCachedSession,
  logout,
  consumeSignOutReason,
};
